import { useMemo } from 'react'
import { formatInTimeZone } from 'date-fns-tz'

import { useDateStore } from '@/components/DateInput'
import { useGetAreasRoutes } from '@/modules/Areas'
import { useReaderLogStore } from '@/modules/FindControllerLog/store'
import { useGarageStore } from '@/modules/FindGarage'
import { useWorkstationStore } from '@/modules/Workstation/store'
import { useAuthStore } from '@/store/auth-store'
import { Role } from '@/types/auth'
import { extractParts, compareValueWithPrefix } from '@/helpers/compareValueWithPrefix'
import { LogSource } from '@/types/logs'
import { endOfDay, format, parse, startOfDay } from 'date-fns'

interface Params {
    isUnfinished?: boolean
    source?: LogSource
    useAreaRoutes?: boolean
}

const sortRoutes = (routes: string[]): string[] => {
    return [...routes].sort((routeA, routeB) => {
        const [prefixA, numA, suffixA] = extractParts(routeA)
        const [prefixB, numB, suffixB] = extractParts(routeB)

        return compareValueWithPrefix([prefixA, numA, suffixA], [prefixB, numB, suffixB])
    })
}

export const useGetControllerLogParams = ({ isUnfinished, source, useAreaRoutes }: Params = {}) => {
    const { routes } = useWorkstationStore()
    const { selectedRoutes } = useGarageStore()
    const { areaRoutes } = useGetAreasRoutes()
    const { source: logSource } = useReaderLogStore()
    const { date } = useDateStore()
    const { role } = useAuthStore()

    const ukDayStart = startOfDay(new Date(date))
    const ukDayEnd = endOfDay(new Date(date))

    const dateFrom = formatInTimeZone(
        parse(`${format(ukDayStart, 'yyyy-MM-dd')}T00:00:00`, "yyyy-MM-dd'T'HH:mm:ss", new Date()),
        'UTC',
        "yyyy-MM-dd'T'HH:mm:ss'Z'"
    )
    const dateTo = formatInTimeZone(
        parse(`${format(ukDayEnd, 'yyyy-MM-dd')}T23:59:59`, "yyyy-MM-dd'T'HH:mm:ss", new Date()),
        'UTC',
        "yyyy-MM-dd'T'HH:mm:ss'Z'"
    )

    return useMemo(() => {
        // Order [data, routes, createdAt, isUnfinished, source, areaCode]
        const formattedDate = formatInTimeZone(date, 'UTC', "yyyy-MM-dd'T'HH:mm:ss")

        if (role === Role.Controller) {
            return [formattedDate, dateFrom, dateTo, sortRoutes(routes.map((route) => route.route))]
        }

        if (role === Role.PerformanceManager) {
            const pmRoutes = useAreaRoutes ? areaRoutes : routes
            return [
                formattedDate,
                dateFrom,
                dateTo,
                sortRoutes(pmRoutes.map((route) => route.route)),
                ,
                isUnfinished,
                source,
            ]
        }

        if (role === Role.MileageClerk) {
            return [formattedDate, dateFrom, dateTo, sortRoutes(selectedRoutes)]
        }

        if (role === Role.Reader) {
            return [
                formattedDate,
                dateFrom,
                dateTo,
                sortRoutes(logSource?.routes.map((route) => route.route) || []) || [],
            ]
        }

        return []
    }, [date, role, selectedRoutes, routes, areaRoutes, logSource, dateTo, dateFrom])
}
